import StorylineImage from "@app/layouts/Article/StorylineElements/Image";
import ContentHubBodyContent from "@app/layouts/ContentHub/Elements/ContentHubBodyContent";
import ContentHubEditorsNote from "@app/layouts/ContentHub/Elements/ContentHubEditorsNote";
import ContentHubFeaturedSection from "@app/layouts/ContentHub/Elements/ContentHubFeaturedSection";
import {
  getCoverCrop,
  getCoverIframeProps,
} from "@app/layouts/ContentHub/utils";
import { ContentHubData } from "@app/types/ServerCustomContext";
import { configContentHub, configSite } from "@pub/config";
import {
  CallOnQuote,
  PageShare,
  ResponsiveIframe,
  ResponsiveImage,
  Typography,
} from "@sphtech/dmg-design-system";

import styles from "./contenthubshadow.module.css";

const { Heading } = Typography;

type ContentHubShadowProps = {
  data: Omit<ContentHubData, "metaData">;
};

const ContentHubShadow = ({ data }: ContentHubShadowProps) => {
  const {
    configBodyContent,
    configEditorsNote,
    configCallQoute,
    configShareButton,
    configFeaturedSection,
  } = configContentHub;

  const coverCrop = getCoverCrop(data.coverImage);

  const coverEmbedProps = getCoverIframeProps(data.coverEmbed);

  return (
    <div className={styles.shadowContainer}>
      <div className={styles.imageContainer}>
        {coverEmbedProps ? (
          <ResponsiveIframe {...coverEmbedProps} />
        ) : (
          coverCrop && (
            <ResponsiveImage
              {...coverCrop}
              displayWidth={1000}
              mobileUp={
                data.coverImage?.crops.original && {
                  ...data.coverImage.crops.original,
                  displayWidth: 1920,
                }
              }
            />
          )
        )}
      </div>

      <div className={styles.primaryContentContainer}>
        {configBodyContent.display && (
          <div className={styles.bodyContentContainer}>
            <ContentHubBodyContent
              data={{
                name: data.name,
                nameZH: data.nameZH,
                storyPostData: data.storyPostData,
              }}
            />
          </div>
        )}
      </div>

      {configEditorsNote.display && (
        <ContentHubEditorsNote editorsNote={data.editorsNote} />
      )}

      {configShareButton.display && (
        <div className={styles.pageShareOuterContainer}>
          {data.sponsorImages && data.sponsorImages.length > 0 && (
            <div className={styles.sponsorImages}>
              <Heading.SubHeading2>
                {data.sponsorImagesLabel}:
              </Heading.SubHeading2>
              {data.sponsorImages.map((sponsorImage, index) => (
                <StorylineImage
                  key={index}
                  element={sponsorImage}
                  bodyMaxWidth={300}
                />
              ))}
            </div>
          )}
          <PageShare
            label={configShareButton.label || "Share this article"}
            url={`${configSite.address}${data.path}`}
            title={data.name}
            size={configSite.pageShare?.size}
            media={configSite.pageShare?.media}
            fill={configSite.pageShare?.fill}
          />
        </div>
      )}

      {configCallQoute.display && (
        <CallOnQuote
          description={
            data.storyPostData?.callOnQoute?.additionalFields.quotee?.value
          }
          title={data.storyPostData?.callOnQoute?.additionalFields.quote?.value}
        />
      )}

      {configFeaturedSection.display && (
        <ContentHubFeaturedSection featuredSection={data.featuredSection} />
      )}
    </div>
  );
};

export default ContentHubShadow;
